import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, Trash, FloppyDisk, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-73.1 — Factory-reset undo snapshots panel.
 *
 * Shown on /employees to super_admins. Lists every current snapshot
 * (auto-purged after 24h) with its actor, row-counts, and a
 * "Restore" button that re-inserts every dumped doc back into Mongo.
 *
 * Kept intentionally minimal — the whole panel is invisible when
 * there are zero snapshots.
 */

function fmtBytes(n) {
  if (!n) return "—";
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

function fmtTimeLeft(expiresAt) {
  if (!expiresAt) return "—";
  const now = new Date();
  const exp = new Date(expiresAt);
  const diffH = (exp - now) / 3600000;
  if (diffH <= 0) return "expired";
  if (diffH < 1) return `${Math.round(diffH * 60)}m left`;
  return `${diffH.toFixed(1)}h left`;
}

export default function FactoryResetSnapshotsPanel({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [typedId, setTypedId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/factory-reset/snapshots");
      setRows(data.snapshots || []);
    } catch (e) {
      // Silent — non-super_admin's page will just show nothing.
      if (e?.response?.status !== 403) {
        toast.error(e?.response?.data?.detail || "Failed to load snapshots");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const doRestore = async (snapshotId) => {
    if (typedId !== snapshotId) {
      toast.error("Type the full snapshot id to unlock restore.");
      return;
    }
    setRestoringId(snapshotId);
    try {
      const { data } = await api.post(
        `/admin/factory-reset/snapshots/${snapshotId}/restore`,
      );
      const total = Object.values(data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(
        `Restored ${total} rows across ${Object.keys(data.restored || {}).length} collections`
        + (data.warnings?.length ? ` (${data.warnings.length} warning${data.warnings.length === 1 ? "" : "s"})` : ""),
      );
      setConfirmId(null);
      setTypedId("");
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || "Restore failed",
      );
    } finally {
      setRestoringId(null);
    }
  };

  const doDelete = async (snapshotId) => {
    if (!confirm(`Delete snapshot ${snapshotId.slice(0, 8)}… permanently?`)) return;
    setDeletingId(snapshotId);
    try {
      await api.delete(`/admin/factory-reset/snapshots/${snapshotId}`);
      toast.success("Snapshot deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  // Hide entirely when there are no snapshots — keeps the page tidy.
  if (!loading && rows.length === 0) return null;

  return (
    <div className="brut-card mt-4" data-testid="snapshots-panel">
      <div className="p-3 border-b-4 border-black flex items-center gap-2 flex-wrap bg-amber-50">
        <FloppyDisk size={16} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">
          Factory-reset snapshots
        </div>
        <span className="label-mono text-zinc-500">
          Auto-purged after 24h
        </span>
        <button
          onClick={load}
          data-testid="snapshots-refresh"
          className="ml-auto border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
        >
          <ArrowClockwise size={10} weight="bold" /> Refresh
        </button>
      </div>

      <div className="p-3 text-[11px] text-zinc-600 leading-relaxed border-b border-zinc-200">
        Every factory reset writes a compressed dump of the swept rows to disk
        BEFORE deleting. If the reset button was clicked in error, pick the
        snapshot below and hit <b>Restore</b> within 24 hours to re-insert every
        row exactly as it was.
      </div>

      {rows.map((s) => (
        <div
          key={s.snapshot_id}
          className="p-3 border-b border-zinc-200 last:border-b-0"
          data-testid={`snapshot-${s.snapshot_id}`}
        >
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <code className="font-mono text-[10px] px-1.5 py-0.5 bg-zinc-100 border border-zinc-300">
              {s.snapshot_id.slice(0, 12)}…
            </code>
            <span className="text-xs">
              <b>{s.total_rows?.toLocaleString?.() ?? s.total_rows}</b> rows
              across <b>{Object.keys(s.counts || {}).length}</b> collections
            </span>
            <span className="ml-auto text-[10px] font-mono text-zinc-500">
              {new Date(s.created_at).toLocaleString()} · {fmtTimeLeft(s.expires_at)}
            </span>
          </div>
          <div className="text-[11px] text-zinc-600 mb-2">
            Triggered by <b>{s.actor?.email || s.actor?.id || "?"}</b>
            {s.keep_audit_log === false && (
              <span className="ml-2 px-1.5 py-0.5 border border-rose-800 bg-rose-50 text-rose-900 text-[9px] uppercase tracking-widest">
                Audit log also wiped
              </span>
            )}
          </div>

          {confirmId === s.snapshot_id ? (
            <div className="space-y-2 border-l-4 border-amber-500 bg-amber-50 p-2">
              <div className="flex items-start gap-2 text-[11px] text-amber-900">
                <Warning size={14} weight="fill" />
                <span>
                  Restore re-inserts every dumped row. Rows added AFTER the
                  reset with the same <code>_id</code> will be skipped. Type the
                  full snapshot id below to confirm.
                </span>
              </div>
              <input
                type="text"
                value={typedId}
                onChange={(e) => setTypedId(e.target.value)}
                placeholder={s.snapshot_id}
                data-testid={`restore-typed-${s.snapshot_id}`}
                className="w-full border border-black p-1 font-mono text-[11px]"
                autoComplete="off"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => doRestore(s.snapshot_id)}
                  disabled={restoringId === s.snapshot_id || typedId !== s.snapshot_id}
                  data-testid={`restore-confirm-${s.snapshot_id}`}
                  className="bg-emerald-700 text-white px-3 py-1.5 text-[11px] uppercase tracking-widest border border-black disabled:opacity-40 inline-flex items-center gap-1"
                >
                  <ArrowClockwise size={12} weight="bold" />
                  {restoringId === s.snapshot_id ? "Restoring…" : "Confirm restore"}
                </button>
                <button
                  onClick={() => { setConfirmId(null); setTypedId(""); }}
                  data-testid={`restore-cancel-${s.snapshot_id}`}
                  className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => { setConfirmId(s.snapshot_id); setTypedId(""); }}
                data-testid={`restore-open-${s.snapshot_id}`}
                className="border border-emerald-700 text-emerald-800 px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-emerald-700 hover:text-white inline-flex items-center gap-1"
              >
                <ArrowClockwise size={12} weight="bold" /> Restore
              </button>
              <button
                onClick={() => doDelete(s.snapshot_id)}
                disabled={deletingId === s.snapshot_id}
                data-testid={`delete-snapshot-${s.snapshot_id}`}
                className="border border-rose-700 text-rose-700 px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-rose-700 hover:text-white disabled:opacity-40 inline-flex items-center gap-1"
              >
                <Trash size={12} weight="bold" /> Delete now
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
